import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../../components/CustomButton';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import {
    useApplyOnboardingImportMutation,
    useGetOnboardingFormatQuery,
    usePreviewOnboardingImportMutation,
} from '../../../api/account/accountApiSlice';
import type {
    OnboardingImportArgs,
    OnboardingImportItem,
    OnboardingImportReport,
} from '../../../api/account/accountApiTypes';
import { organizationApiSlice } from '../../../api/organization/organizationApiSlice';
import { groupAdminApiSlice } from '../../../api/group/groupAdminApiSlice';
import { locationsApiSlice } from '../../../api/locations/locationsApiSlice';
import { tenantSelector } from '../../../selector/authenticationSelector';
import { useViewedTenantAccount } from '../../../hooks/useViewedTenantAccount';
import { useViewedSetupProgress } from '../../../hooks/useSetupProgress';
import { paths } from '../../../routes';
import AttachmentUploads, { UploadTask } from './AttachmentUploads';
import OpenPointsList from '../../../components/importReport/OpenPointsList';
import ImportItemsTable from '../../../components/importReport/ImportItemsTable';
import { openPointsOf, setupSnapshotOf } from '../../../components/importReport/openPoints';
import {
    IMPORT_GROUPS,
    countByAction,
    countSkipped,
    downloadText,
    errorKeyOf,
    groupItems,
    isTextFileName,
    isUnanswered,
    promptWithSchema,
    shapeProblemText,
} from '../../../components/importReport/importReportView';
import { ONBOARDING_FILE_ACCEPT, uploadTasksOf } from './onboardingImportView';

const card = 'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4 flex flex-col gap-3';

interface FileState {
    name: string;
    text: string;
}

/**
 * "Einrichtung per Datei" (ADR-0003 amendment 2026-10-29, plan
 * docs/plans/onboarding-import.md): one onboarding file fills the account.
 * Upload → preview (grouped, errors and warnings per item, "manuell anlegen")
 * → photos and the Nutzungsordnung attached on the preview → Übernehmen.
 * The owner works on their own account; a master on the tenant in view.
 * Nothing is kept between visits: the browser holds the file until applied.
 */
const OnboardingImportContent: React.FC<{ slug: string | undefined }> = ({ slug }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const fileInput = useRef<HTMLInputElement>(null);
    const format = useGetOnboardingFormatQuery();
    const [file, setFile] = useState<FileState | null>(null);
    const [photos, setPhotos] = useState<Record<string, File[]>>({});
    const [termsOfUse, setTermsOfUse] = useState<File | null>(null);
    const [report, setReport] = useState<OnboardingImportReport | null>(null);
    const [result, setResult] = useState<OnboardingImportReport | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [unanswered, setUnanswered] = useState(false);
    const [uploads, setUploads] = useState<UploadTask[]>([]);
    const [copied, setCopied] = useState(false);
    const [preview, { isLoading: previewing }] = usePreviewOnboardingImportMutation();
    const [apply, { isLoading: applying }] = useApplyOnboardingImportMutation();
    // The dashboard's checklist answers what the account still lacks (one rule,
    // one place) — for the tenant IN VIEW, so a master sees that tenant's gaps.
    const progress = useViewedSetupProgress();
    const { accountTenant } = useViewedTenantAccount();
    const setup = setupSnapshotOf(progress.state === 'ready' ? progress.progress : null, accountTenant);

    const failureText = (err: unknown): string => {
        const key = errorKeyOf(err);
        const server = (err as { data?: { message?: unknown } } | undefined)?.data?.message;
        return key ? t(key) : t('onboarding_import.error.generic', { detail: typeof server === 'string' ? server : '' });
    };

    const runPreview = async (next: Partial<OnboardingImportArgs> = {}) => {
        const args: OnboardingImportArgs = { slug, fileText: file?.text ?? '', photos, termsOfUse, ...next };
        if (!args.fileText) return;
        setError(null);
        try {
            setReport(await preview(args).unwrap());
        } catch (err) {
            // Not an onboarding file at all: the answer still says why.
            const details = (err as { data?: { details?: OnboardingImportReport } } | undefined)?.data?.details;
            setReport(details ?? null);
            setError(failureText(err));
        }
    };

    const chooseFile = async (chosen: File | undefined) => {
        setError(null);
        setResult(null);
        setReport(null);
        setPhotos({});
        setTermsOfUse(null);
        if (!chosen) {
            setFile(null);
            return;
        }
        try {
            const text = await chosen.text();
            setFile({ name: chosen.name, text });
            await runPreview({ fileText: text, photos: {}, termsOfUse: null });
        } catch {
            setFile(null);
            setError(t('onboarding_import.error.unreadable'));
        }
    };

    const attachPhotos = (room: string, files: FileList | null) => {
        const next = { ...photos, [room]: Array.from(files ?? []) };
        setPhotos(next);
        runPreview({ photos: next });
    };

    const attachTerms = (files: FileList | null) => {
        const next = files?.[0] ?? null;
        setTermsOfUse(next);
        runPreview({ termsOfUse: next });
    };

    const runApply = async () => {
        if (!file) return;
        setError(null);
        setUnanswered(false);
        try {
            const written = await apply({ slug, fileText: file.text, photos, termsOfUse }).unwrap();
            // The attachments follow now, one by one, through their own routes.
            setUploads(uploadTasksOf(written, photos, termsOfUse));
            // Rooms, prices, classes and organisations live in other slices.
            dispatch(organizationApiSlice.util.invalidateTags(['organizationList', 'customerClassList']));
            dispatch(groupAdminApiSlice.util.invalidateTags(['AdminGroups', 'Products', 'PricingStructure']));
            dispatch(locationsApiSlice.util.invalidateTags(['locationList', 'Groups']));
            setResult(written);
            setReport(null);
            setFile(null);
            setPhotos({});
            setTermsOfUse(null);
            if (fileInput.current) fileInput.current.value = '';
        } catch (err) {
            // Not an onboarding file at all (400 `not_onboarding_file`): the refusal carries the report saying why.
            const details = (err as { data?: { details?: OnboardingImportReport } } | undefined)?.data?.details;
            if (details) setReport(details);
            // No answer: the import may have committed. Re-applying is safe (it
            // updates by name), so the way on is to check the file again.
            if (isUnanswered(err)) setUnanswered(true);
            else setError(failureText(err));
        }
    };

    const formatData = format.data;
    const copyPrompt = async () => {
        if (!formatData) return;
        try {
            await navigator.clipboard.writeText(promptWithSchema(formatData));
            setCopied(true);
        } catch {
            setError(t('onboarding_import.error.copy'));
        }
    };

    // An item with an error is skipped and the rest applied (PO, 2026-10-30);
    // only a file that is not the format at all cannot be applied.
    const readable = !!report && report.shapeProblems.length === 0;
    // While an answer is missing or a re-check runs, the report on screen may
    // be stale: the only way on is "Erneut prüfen", never a second apply.
    const canApply = readable && !unanswered && !previewing;
    const groups = report ? groupItems(report.items) : [];
    const skippedCount = report ? countSkipped(report.items) : 0;

    const renderAttach = (item: OnboardingImportItem) => {
        // A skipped room is not written, so its photos would have nowhere to go.
        if (item.section === 'room' && !item.skipped) {
            return (
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('onboarding_import.attach_photos')}
                    <input
                        type={'file'}
                        accept={'image/*'}
                        multiple
                        className={'text-xs text-foreground'}
                        onChange={(event) => attachPhotos(item.key, event.target.files)}
                    />
                    {(photos[item.key]?.length ?? 0) > 0 && (
                        <span>{t('onboarding_import.photos_attached', { count: photos[item.key]?.length ?? 0 })}</span>
                    )}
                </label>
            );
        }
        if (item.section === 'document' && !item.skipped) {
            return (
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('onboarding_import.attach_pdf')}
                    <input
                        type={'file'}
                        accept={'application/pdf,.pdf'}
                        className={'text-xs text-foreground'}
                        onChange={(event) => attachTerms(event.target.files)}
                    />
                </label>
            );
        }
        return null;
    };

    return (
        <div className={'flex flex-col gap-5'}>
            <PageHeader>
                <div className={'flex-grow'}>
                    <PageHeadline>{t('onboarding_import.title')}</PageHeadline>
                    <p className={'text-sm text-foreground/60'}>{t('onboarding_import.subtitle')}</p>
                </div>
                <CustomButton title={t('onboarding_import.back_to_account')} isFilled={false} onClick={() => navigate(paths.account)} />
            </PageHeader>

            <section className={card}>
                <h2 className={'text-base font-semibold text-foreground'}>{t('onboarding_import.prompt_title')}</h2>
                <p className={'text-sm text-foreground/80'}>{t('onboarding_import.prompt_hint')}</p>
                {format.isLoading && <p className={'text-sm text-foreground/60'}>{t('onboarding_import.prompt_loading')}</p>}
                {format.isError && <p role={'alert'} className={'text-sm text-foreground'}>{t('onboarding_import.error.format')}</p>}
                {formatData && (
                    <>
                        <pre className={'max-h-48 overflow-auto whitespace-pre-wrap rounded-[5px] bg-foreground/5 p-3 text-xs text-foreground'}>
                            {formatData.prompt}
                        </pre>
                        <div className={'flex flex-wrap gap-3'}>
                            <CustomButton title={t(copied ? 'onboarding_import.prompt_copied' : 'onboarding_import.prompt_copy')} onClick={copyPrompt} />
                            <CustomButton
                                title={t('onboarding_import.prompt_download')}
                                isFilled={false}
                                onClick={() => downloadText(promptWithSchema(formatData), t('onboarding_import.prompt_file'), 'text/plain')}
                            />
                            <CustomButton
                                title={t('onboarding_import.example_download')}
                                isFilled={false}
                                onClick={() => downloadText(
                                    `${JSON.stringify(formatData.example, null, 2)}\n`,
                                    t('onboarding_import.example_file'),
                                    'application/json',
                                )}
                            />
                            {formatData.exampleApartments !== undefined && (
                                <CustomButton
                                    title={t('onboarding_import.example_apartments_download')}
                                    isFilled={false}
                                    onClick={() => downloadText(
                                        `${JSON.stringify(formatData.exampleApartments, null, 2)}\n`,
                                        t('onboarding_import.example_apartments_file'),
                                        'application/json',
                                    )}
                                />
                            )}
                        </div>
                    </>
                )}
            </section>

            <section className={card}>
                <h2 className={'text-base font-semibold text-foreground'}>{t('onboarding_import.upload_title')}</h2>
                <p className={'text-sm text-foreground/80'}>{t('onboarding_import.upload_hint')}</p>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('onboarding_import.file')}
                    <input
                        ref={fileInput}
                        type={'file'}
                        accept={ONBOARDING_FILE_ACCEPT}
                        className={'text-sm text-foreground'}
                        onChange={(event) => { chooseFile(event.target.files?.[0]); }}
                    />
                </label>
                {file && (
                    <p className={'text-sm text-foreground/80'}>
                        {t('onboarding_import.file_chosen', { name: file.name })}
                        {isTextFileName(file.name) && ` ${t('onboarding_import.file_txt_ok')}`}
                    </p>
                )}
                {previewing && <p className={'text-sm text-foreground/60'}>{t('onboarding_import.checking')}</p>}
                {error && <p role={'alert'} className={'text-sm text-foreground'}>{error}</p>}
            </section>
            {unanswered && (
                <section className={card} role={'alert'}>
                    <p className={'text-sm text-foreground'}>{t('onboarding_import.unanswered')}</p>
                    <div>
                        <CustomButton
                            title={t('onboarding_import.check_again')}
                            loading={previewing}
                            onClick={() => { setUnanswered(false); runPreview(); }}
                        />
                    </div>
                </section>
            )}

            {report && report.shapeProblems.length > 0 && (
                <section className={card} role={'alert'}>
                    <h2 className={'text-base font-semibold text-foreground'}>{t('onboarding_import.not_file_title')}</h2>
                    <p className={'text-sm text-foreground/80'}>{t('onboarding_import.shape_hint')}</p>
                    <ul className={'list-disc pl-5 text-sm text-foreground/80'}>
                        {report.shapeProblems.map((p) => (
                            <li key={`${p.path}-${p.code}`}>{shapeProblemText(p, t)}</li>
                        ))}
                    </ul>
                </section>
            )}

            {report && report.shapeProblems.length === 0 && (
                <>
                    <p className={'text-sm text-foreground'}>
                        {t('onboarding_import.summary', { skipped: skippedCount, warnings: report.warnings })}
                    </p>
                    {groups.map(({ group, items }) => (
                        <ImportItemsTable
                            key={group}
                            title={t(`onboarding_import.group.${group}`)}
                            items={items}
                            extraColumn={{ header: t('onboarding_import.column.attachments'), render: renderAttach }}
                        />
                    ))}
                    <OpenPointsList
                        points={openPointsOf(report, setup, t, i18n.language)}
                        hint={t('onboarding_import.open_points.hint_preview')}
                        setupState={progress.state}
                    />
                    <div className={'flex flex-wrap gap-3'}>
                        <CustomButton title={t('onboarding_import.apply')} loading={applying} disabled={!canApply} onClick={runApply} />
                    </div>
                </>
            )}

            {result && (
                <section className={card}>
                    <h2 className={'text-base font-semibold text-foreground'}>{t('onboarding_import.result_title')}</h2>
                    <ul className={'list-disc pl-5 text-sm text-foreground/80'}>
                        {IMPORT_GROUPS.map((group) => {
                            const counts = countByAction(result.items, group);
                            if (counts.create + counts.update === 0) return null;
                            return (
                                <li key={group}>
                                    {t('onboarding_import.result_line', {
                                        group: t(`onboarding_import.group.${group}`), created: counts.create, updated: counts.update,
                                    })}
                                </li>
                            );
                        })}
                    </ul>
                    {countSkipped(result.items) > 0 && (
                        <p className={'text-sm font-medium text-foreground'}>
                            {t('onboarding_import.result_skipped', { count: countSkipped(result.items) })}
                        </p>
                    )}
                    <AttachmentUploads tasks={uploads} slug={slug} />
                    <div className={'flex flex-wrap gap-3'}>
                        <CustomButton title={t('common.back_to_dashboard')} isFilled={false} onClick={() => navigate(paths.dashboard)} />
                    </div>
                </section>
            )}
            {result && (
                <OpenPointsList
                    points={openPointsOf(result, setup, t, i18n.language)}
                    hint={t('onboarding_import.open_points.hint_result')}
                    setupState={progress.state}
                />
            )}
        </div>
    );
};

/** The page: one content instance per tenant in view, so a switch starts clean (#909/#929). */
const OnboardingImportPage: React.FC = () => {
    const selectedTenant = useSelector(tenantSelector);
    const { isMaster, ownsViewedTenant } = useViewedTenantAccount();
    const slug = isMaster && !ownsViewedTenant ? selectedTenant || undefined : undefined;
    return <OnboardingImportContent key={selectedTenant ?? ''} slug={slug} />;
};

export default OnboardingImportPage;
